/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetDeviceAgentConfigurationRequest,
  GetDeviceAgentConfigurationSecurity,
} from "../models/operations/getdeviceagentconfiguration.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDeviceAgentConfigurationQuery,
  DeviceAgentConfigurationQueryData,
  prefetchDeviceAgentConfiguration,
  queryKeyDeviceAgentConfiguration,
} from "./deviceAgentConfiguration.core.js";
export {
  buildDeviceAgentConfigurationQuery,
  type DeviceAgentConfigurationQueryData,
  prefetchDeviceAgentConfiguration,
  queryKeyDeviceAgentConfiguration,
};

export type DeviceAgentConfigurationQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getConfiguration agent
 *
 * @remarks
 * Get the organization-wide device-agent configuration for the dashboard. Requires a session with the org:admin scope. An unconfigured organization returns an empty document with is_configured=false; enrolled agents do not receive a remote layer until an administrator saves one.
 */
export function useDeviceAgentConfiguration(
  request?: GetDeviceAgentConfigurationRequest | undefined,
  security?: GetDeviceAgentConfigurationSecurity | undefined,
  options?: QueryHookOptions<
    DeviceAgentConfigurationQueryData,
    DeviceAgentConfigurationQueryError
  >,
): UseQueryResult<
  DeviceAgentConfigurationQueryData,
  DeviceAgentConfigurationQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDeviceAgentConfigurationQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getConfiguration agent
 *
 * @remarks
 * Get the organization-wide device-agent configuration for the dashboard. Requires a session with the org:admin scope. An unconfigured organization returns an empty document with is_configured=false; enrolled agents do not receive a remote layer until an administrator saves one.
 */
export function useDeviceAgentConfigurationSuspense(
  request?: GetDeviceAgentConfigurationRequest | undefined,
  security?: GetDeviceAgentConfigurationSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DeviceAgentConfigurationQueryData,
    DeviceAgentConfigurationQueryError
  >,
): UseSuspenseQueryResult<
  DeviceAgentConfigurationQueryData,
  DeviceAgentConfigurationQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDeviceAgentConfigurationQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDeviceAgentConfigurationData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: DeviceAgentConfigurationQueryData,
): DeviceAgentConfigurationQueryData | undefined {
  const key = queryKeyDeviceAgentConfiguration(...queryKeyBase);

  return client.setQueryData<DeviceAgentConfigurationQueryData>(key, data);
}

export function invalidateDeviceAgentConfiguration(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agent", "getConfiguration", ...queryKeyBase],
  });
}

export function invalidateAllDeviceAgentConfiguration(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "agent", "getConfiguration"],
  });
}
